// Mocks for /api/secure/me/diet/*

import { delay } from './index'

const STORAGE_KEY = 'patientPortal.mock.diet.v1'

function readStore() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
  } catch (e) {
    return {}
  }
}
function writeStore(s) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
  } catch (e) {
    /* ignore */
  }
}

function seedFor(profile) {
  if (!profile) return { plan: null, log: [] }
  const isSelf = profile.relation === 'SELF'
  const plan = isSelf
    ? {
        id: 'diet_self_apr',
        name: 'April plan',
        description: 'Mediterranean-leaning, dinner before 8pm.',
        recommendedMealsByType: {
          BREAKFAST: ['Oats + fruit', 'Eggs + multigrain toast'],
          LUNCH: ['Whole-grain rice + lentils + greens', 'Roti + chicken + salad'],
          SNACK: ['Handful of nuts', 'Yogurt + berries'],
          DINNER: ['Grilled fish + veg', 'Soup + small grain bowl'],
        },
        restrictions: ['Limit refined sugar', 'Limit deep fried'],
        hydrationGoalMl: 2500,
        prescribedBy: 'Dr. Neha Bose',
      }
    : {
        id: 'diet_mom_dm',
        name: 'Diabetes-friendly plan',
        description: 'Lower carb, distributed across day.',
        recommendedMealsByType: {
          BREAKFAST: ['Oats + nuts (no sugar)', 'Vegetable upma'],
          LUNCH: ['Roti + sabzi + dal + salad'],
          DINNER: ['Vegetable curry + small portion of rice/roti'],
        },
        restrictions: ['No added sugar', 'No fruit juices', 'Avoid white rice'],
        hydrationGoalMl: 2000,
        prescribedBy: 'Dr. Rao',
      }
  return { plan, log: [] }
}

function ensure(profile) {
  const store = readStore()
  if (!store[profile.id]) {
    store[profile.id] = seedFor(profile)
    writeStore(store)
  }
  return { store, prof: store[profile.id] }
}

export async function getPlan(profile) {
  await delay()
  if (!profile) return null
  const { prof } = ensure(profile)
  return prof.plan
}

export async function getToday(profile) {
  await delay()
  if (!profile) return null
  const { prof } = ensure(profile)
  const todayStr = new Date().toISOString().slice(0, 10)
  const slots = ['BREAKFAST', 'LUNCH', 'SNACK', 'DINNER'].map((mealType) => {
    const matches = prof.log.filter(
      (e) => e.mealType === mealType && (e.takenAt || '').slice(0, 10) === todayStr,
    )
    const latest = matches[matches.length - 1]
    const recommended =
      prof.plan?.recommendedMealsByType?.[mealType] || null
    let totalCalories = null
    if (latest?.entries?.length) {
      let sum = 0
      let any = false
      for (const e of latest.entries) {
        if (e?.approxCalories != null) {
          sum += e.approxCalories
          any = true
        }
      }
      if (any) totalCalories = sum
    }
    return {
      mealType,
      mealLabel:
        mealType === 'BREAKFAST'
          ? 'Breakfast'
          : mealType === 'LUNCH'
          ? 'Lunch'
          : mealType === 'SNACK'
          ? 'Snack'
          : 'Dinner',
      logged: !!latest,
      summary: latest?.items?.join(', ') || null,
      entries: latest?.entries || null,
      photoUrl: latest?.photoUrl || null,
      totalCalories,
      recommendedItems: recommended,
    }
  })
  return {
    slots,
    hydrationGoalMl: prof.plan?.hydrationGoalMl || null,
    hydrationLoggedMl: null,
  }
}

export async function log(profile, body) {
  await delay()
  if (!profile) return null
  const { store, prof } = ensure(profile)
  // Mirror the real backend's entries-prefer-over-items merging so the mock
  // response shape matches what 9.0.493+ clients expect to read back.
  const entries = Array.isArray(body.entries) && body.entries.length > 0
    ? body.entries
        .map((e) => ({
          code: e.code || null,
          displayName: (e.displayName || e.code || '').trim(),
          quantity: e.quantity || null,
          approxCalories: e.approxCalories ?? null,
          photoUrl: e.photoUrl || null,
          notes: e.notes || null,
        }))
        .filter((e) => e.displayName)
    : (body.items || []).map((s) => ({ displayName: s }))
  const items = entries.map((e) => e.displayName)
  const entry = {
    id: `meal_${Date.now()}`,
    mealType: body.mealType,
    items,
    entries,
    quantity: body.quantity || null,
    photoUrl: body.photoUrl || null,
    additionalPhotoUrls: body.additionalPhotoUrls || null,
    note: body.note || null,
    takenAt: body.takenAt || new Date().toISOString(),
  }
  prof.log.push(entry)
  store[profile.id] = prof
  writeStore(store)
  return entry
}

// -------- Food catalog mock --------

const FOOD_CATALOG_MOCK = [
  // A small but representative subset — enough to show the UI works in mocks.
  { code: 'IDLI', displayName: 'Idli', category: 'BREAKFAST', cuisine: 'INDIAN',
    tags: ['VEG', 'STEAMED'], defaultQuantityLabel: '2 pieces', approxCalories: 78 },
  { code: 'DOSA_MASALA', displayName: 'Masala dosa', category: 'BREAKFAST', cuisine: 'INDIAN',
    tags: ['VEG'], defaultQuantityLabel: '1 piece', approxCalories: 250 },
  { code: 'VEG_POHA', displayName: 'Vegetable poha', category: 'BREAKFAST', cuisine: 'INDIAN',
    tags: ['VEG'], defaultQuantityLabel: '1 cup', approxCalories: 180 },
  { code: 'ALOO_PARATHA', displayName: 'Aloo paratha', category: 'BREAKFAST', cuisine: 'INDIAN',
    tags: ['VEG'], defaultQuantityLabel: '1 piece', approxCalories: 290 },
  { code: 'DAL_TADKA', displayName: 'Dal tadka', category: 'MAIN', cuisine: 'INDIAN',
    tags: ['VEG', 'PROTEIN'], defaultQuantityLabel: '1 bowl', approxCalories: 180 },
  { code: 'PANEER_BUTTER_MASALA', displayName: 'Paneer butter masala', category: 'MAIN',
    cuisine: 'INDIAN', tags: ['VEG', 'DAIRY'], defaultQuantityLabel: '1 bowl', approxCalories: 380 },
  { code: 'CHICKEN_BIRYANI', displayName: 'Chicken biryani', category: 'MAIN', cuisine: 'INDIAN',
    tags: ['NON_VEG', 'RICE'], defaultQuantityLabel: '1 plate', approxCalories: 580 },
  { code: 'ROTI', displayName: 'Chapati / roti', category: 'BREAD', cuisine: 'INDIAN',
    tags: ['VEG'], defaultQuantityLabel: '1 piece', approxCalories: 80 },
  { code: 'RICE_STEAMED', displayName: 'Steamed rice', category: 'BREAD', cuisine: 'INDIAN',
    tags: ['VEG', 'RICE'], defaultQuantityLabel: '1 cup', approxCalories: 200 },
  { code: 'SAMOSA', displayName: 'Samosa', category: 'SNACK', cuisine: 'INDIAN',
    tags: ['VEG', 'FRIED'], defaultQuantityLabel: '1 piece', approxCalories: 260 },
  { code: 'CHAI_MASALA', displayName: 'Masala chai', category: 'DRINK', cuisine: 'INDIAN',
    tags: ['DAIRY'], defaultQuantityLabel: '1 cup', approxCalories: 90 },
  { code: 'GULAB_JAMUN', displayName: 'Gulab jamun', category: 'SWEET', cuisine: 'INDIAN',
    tags: ['VEG', 'DAIRY', 'SWEET'], defaultQuantityLabel: '2 pieces', approxCalories: 280 },
  { code: 'APPLE', displayName: 'Apple', category: 'FRUIT', cuisine: 'INDIAN',
    tags: ['VEG', 'VEGAN', 'FRUIT'], defaultQuantityLabel: '1 medium', approxCalories: 95 },
  { code: 'BANANA', displayName: 'Banana', category: 'FRUIT', cuisine: 'INDIAN',
    tags: ['VEG', 'VEGAN', 'FRUIT'], defaultQuantityLabel: '1 medium', approxCalories: 105 },
]

export async function searchFoodCatalog(profile, opts = {}) {
  await delay(80)
  const q = (opts.q || '').toLowerCase().trim()
  const cat = (opts.category || '').toUpperCase().trim()
  const limit = Math.max(1, Math.min(opts.limit || 20, 50))

  // Mirror the server's dietary filter so the mock-mode picker behaves the
  // same way: explicit `diet` wins; else read the patient's stored
  // preference from the preferences mock; `allDiets:true` bypasses entirely.
  let requireTags = []
  let excludeTags = []
  if (!opts.allDiets) {
    let diet = opts.diet
    if (!diet) {
      try {
        const raw = JSON.parse(localStorage.getItem('patientPortal.mock.preferences.v1') || '{}')
        diet = raw[profile?.id]?.dietaryPreference || null
        const xs = raw[profile?.id]?.foodTagsToExclude
        if (Array.isArray(xs)) {
          excludeTags = excludeTags.concat(xs.map((t) => String(t).toUpperCase()))
        }
      } catch (e) {
        diet = null
      }
    }
    if (diet) {
      const d = String(diet).toUpperCase()
      if (d === 'VEGETARIAN' || d === 'JAIN') {
        requireTags = ['VEG']
        excludeTags = excludeTags.concat(['NON_VEG', 'EGG'])
      } else if (d === 'VEGAN') {
        requireTags = ['VEGAN']
        excludeTags = excludeTags.concat(['NON_VEG', 'EGG', 'DAIRY'])
      } else if (d === 'EGGETARIAN') {
        excludeTags = excludeTags.concat(['NON_VEG'])
      }
      // PESCATARIAN / NON_VEGETARIAN — no filter (matches backend).
    }
  }

  const filtered = FOOD_CATALOG_MOCK.filter((it) => {
    if (cat && it.category !== cat) return false
    const tags = (it.tags || []).map((t) => t.toUpperCase())
    if (requireTags.length > 0 && !requireTags.every((t) => tags.includes(t))) return false
    if (excludeTags.length > 0 && excludeTags.some((t) => tags.includes(t))) return false
    if (!q) return true
    const hay = (it.displayName + ' ' + (it.tags || []).join(' ') + ' ' + it.category)
      .toLowerCase()
    return q.split(/\s+/).filter(Boolean).every((tok) => hay.includes(tok))
  })
  return filtered.slice(0, limit)
}

export async function uploadDietPhoto(profile, file) {
  // Mock fallback: read the photo to a data URL so the UI can show a
  // preview without a real upload roundtrip. In dev this is fine; in prod
  // shouldUseMocks() is false and the real multipart endpoint is hit.
  await delay(200)
  if (!file) return null
  const url = await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error)
    reader.onload = () => resolve(reader.result)
    reader.readAsDataURL(file)
  })
  return {
    fileId: `mock_${Date.now()}`,
    url,
    mimeType: file.type || 'image/jpeg',
    sizeBytes: file.size || 0,
  }
}

export async function listLog(profile, from, to) {
  await delay()
  if (!profile) return []
  const { prof } = ensure(profile)
  const fromMs = from ? Date.parse(from) : 0
  const toMs = to ? Date.parse(to) : Number.POSITIVE_INFINITY
  return prof.log
    .filter((e) => {
      const t = Date.parse(e.takenAt || 0)
      return t >= fromMs && t < toMs
    })
    .sort((a, b) => (b.takenAt || '').localeCompare(a.takenAt || ''))
}

export function __resetDietMock() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    /* ignore */
  }
}
